import { useEffect, useState, useRef } from 'react';
import { pixelAchat } from '../lib/pixels';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEXTES = {
  fr: {
    verification: 'Vérification du paiement…',
    patienter: 'Nous vérifions votre paiement, veuillez patienter.',
    paiementLivraison: 'Paiement à la livraison',
    paiementRetrait: 'Paiement au retrait',
    preparez: 'Préparez le montant exact.',
    erreur: 'Une erreur est survenue',
    nonVerifie: "Nous n'avons pas pu vérifier votre paiement. Contactez-nous, nous vous aiderons.",
  },
  en: {
    verification: 'Checking your payment…',
    patienter: 'We are checking your payment, please wait.',
    paiementLivraison: 'Payment on delivery',
    paiementRetrait: 'Payment on collection',
    preparez: 'Please have the exact amount ready.',
    erreur: 'Something went wrong',
    nonVerifie: 'We could not verify your payment. Please contact us for help.',
  },
};

export default function CheckoutSuccess() {
  const { t, language } = useLanguage();
  const x = TEXTES[language] || TEXTES.fr;
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const method = searchParams.get('method');

  useEffect(() => {
    // Clear cart on success page load
    clearCart();

    // If coming from Stripe, poll for payment status
    if (sessionId) {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) {
          setPaymentStatus('timeout');
          return;
        }

        try {
          const response = await axios.get(`${API}/payments/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            setPaymentStatus('success');
          } else if (response.data.status === 'expired') {
            setPaymentStatus('expired');
          } else {
            // Continue polling
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } catch (error) {
          // Error handled silently
          setPaymentStatus('error');
        }
      };

      pollStatus();
    } else if (method === 'mollie' && orderId) {
      // Retour de Mollie : on interroge le statut (le webhook peut être en retard)
      const pollMollie = async (attempts = 0) => {
        if (attempts >= 6) {
          setPaymentStatus('timeout');
          return;
        }
        try {
          const response = await axios.get(`${API}/payments/mollie/status/${orderId}`);
          if (response.data.payment_status === 'paid') {
            setPaymentStatus('success');
          } else {
            setTimeout(() => pollMollie(attempts + 1), 2000);
          }
        } catch (error) {
          setPaymentStatus('error');
        }
      };
      pollMollie();
    } else if (method === 'cash' || method === 'paypal') {
      setPaymentStatus('success');
    }
  }, [sessionId, orderId, method, clearCart]);

  // Conversion publicitaire, une fois le paiement confirmé. Le montant est
  // relu depuis la commande côté serveur : le panier vient d'être vidé, et
  // un montant venu du navigateur ne serait pas fiable de toute façon.
  // Meta et Google dédoublonnent sur l'identifiant de commande, donc un
  // rechargement de page ne compte pas deux fois la vente.
  const [commande, setCommande] = useState(null);
  const achatCompte = useRef(false);
  useEffect(() => {
    if (paymentStatus !== 'success' || !orderId || achatCompte.current) return;
    achatCompte.current = true;
    axios.get(`${API}/orders/${orderId}`)
      .then(({ data }) => {
        setCommande(data);
        pixelAchat({ commandeId: orderId, total: Number(data.total_amount) || 0 });
      })
      .catch(() => {});
  }, [paymentStatus, orderId]);
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-lg mx-auto px-4 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', damping: 15 }}
          className="w-24 h-24 rounded-full bg-secondary mx-auto flex items-center justify-center mb-6"
        >
          {paymentStatus === 'checking' ? (
            <Package className="w-12 h-12 text-secondary_hover animate-pulse" />
          ) : paymentStatus === 'success' ? (
            <CheckCircle className="w-12 h-12 text-secondary_hover" />
          ) : (
            <Package className="w-12 h-12 text-secondary_hover" />
          )}
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {paymentStatus === 'checking' ? (
            <>
              <h1 className="text-3xl font-bold font-heading text-text_primary mb-4">
                {x.verification}
              </h1>
              <p className="text-text_secondary mb-8">
                {x.patienter}
              </p>
            </>
          ) : paymentStatus === 'success' ? (
            <>
              <h1 className="text-3xl font-bold font-heading text-text_primary mb-4" data-testid="success-title">
                {t('orderSuccess')}
              </h1>
              <p className="text-text_secondary mb-8">
                {t('orderSuccessMessage')}
              </p>
              {method === 'cash' && (
                <div className="bg-accent/20 p-4 rounded-xl mb-8">
                  <p className="text-sm text-text_primary">
                    <strong>
                      {commande?.mode_retrait === 'magasin' ? x.paiementRetrait : x.paiementLivraison}
                    </strong> — {x.preparez}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold font-heading text-text_primary mb-4">
                {x.erreur}
              </h1>
              <p className="text-text_secondary mb-8">
                Votre paiement n'a pas pu être vérifié. Contactez-nous pour assistance.
              </p>
            </>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary_hover transition-all"
              data-testid="back-to-home-button"
            >
              {t('backToHome')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-secondary/50 text-text_primary font-semibold rounded-full hover:bg-secondary transition-all"
            >
              {t('continueShopping')}
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
